/**
 * Mobile-optimized wallet view — renders a beautiful shareable digital ID card
 * addressable by serial. Designed for /wallet/:serial public route.
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, IdCard, CheckCircle2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WalletCardPage() {
  const { serial } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['wallet-card', serial],
    queryFn: async () => {
      const res = await fetch(`${API}/api/public/wallet/card/${serial}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Card not found');
      return d;
    },
    retry: false,
  });

  if (isLoading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="wallet-error">
        <div className="bg-red-900/40 border border-red-700 rounded-xl p-6 max-w-md text-center text-white">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-2" />
          <p className="text-lg font-semibold">Card Not Found</p>
          <p className="text-sm text-red-200 mt-1">{error?.message || 'Invalid serial or the card has been revoked.'}</p>
        </div>
      </div>
    );
  }
  const r = data.recipient || {};
  const inst = data.institution || {};
  const t = data.template || {};
  const verifyUrl = `${window.location.origin}/api/public/alumni/verify?admission_number=${r.admission_number || ''}`;
  const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(verifyUrl)}`;
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-800 p-4 flex items-center justify-center" data-testid="wallet-page">
      <div className="w-full max-w-sm">
        <div className="rounded-3xl overflow-hidden shadow-2xl bg-white" data-testid="wallet-card">
          <div className="p-6 text-white relative" style={{ background: `linear-gradient(135deg, ${t.bg_color || inst.primary_color}, ${t.bg_color || inst.primary_color}dd)` }}>
            <div className="flex items-center gap-3">
              {inst.logo_url && <img src={inst.logo_url} alt="" className="h-12 w-12 rounded-lg bg-white/15 object-contain p-1" />}
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-widest opacity-80">Digital ID</p>
                <p className="font-bold">{inst.name}</p>
              </div>
              <IdCard className="h-5 w-5 opacity-60" />
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 text-xl font-bold">
                {r.photo_url ? <img src={r.photo_url} alt="" className="h-full w-full object-cover rounded-xl" /> : (r.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="flex-1">
                <p className="text-lg font-bold text-slate-900">{r.name}</p>
                <p className="text-xs text-slate-500">ID: <span className="font-mono">{r.admission_number}</span></p>
                <p className="text-[10px] text-slate-400 capitalize">{data.role} · {data.status}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs border-t pt-3">
              <div>
                <p className="text-slate-400 uppercase text-[9px]">Expires</p>
                <p className="font-semibold">{data.expires_at || 'Indefinite'}</p>
              </div>
              <div>
                <p className="text-slate-400 uppercase text-[9px]">Status</p>
                <p className="font-semibold text-emerald-600"><CheckCircle2 className="h-3 w-3 inline mr-0.5" />Active</p>
              </div>
            </div>
            {(data.access_zones || []).length > 0 && (
              <div>
                <p className="text-slate-400 uppercase text-[9px] mb-1">Authorised Zones</p>
                <div className="flex flex-wrap gap-1">
                  {data.access_zones.map(z => (
                    <span key={z} className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{z.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-center pt-3 border-t">
              <img src={qrImg} alt="QR" className="w-40 h-40" />
            </div>
            <p className="text-center text-[10px] text-slate-400">Serial · <span className="font-mono">{data.serial}</span></p>
          </div>
        </div>
        <p className="text-center text-[11px] text-slate-500 mt-3">Save this page to your phone's home screen for one-tap access.</p>
      </div>
    </div>
  );
}
